/**
 * The role library, as the admin app sees it. Mirrors KINDS in
 * droomworkApi/app/Services/Library/LibraryService.js: one entry per level,
 * each pointing at its parent. Everything on the page derives from this
 * map: the tabs, the table columns, the cascading dropdowns in the form and
 * the filter, and the row actions. Adding a level is one entry here plus
 * one on the server.
 *
 *   category -> role -> specialty -> tool
 *                    -> skill        (a skill may also be generic: no role)
 */
export const LIBRARY = {
  category: {
    label: 'Category',
    plural: 'Categories',
    hint: 'An industry group, e.g. Engineering or Finance',
    parent: null,
    fields: ['name', 'description'],
  },
  role: {
    label: 'Role',
    plural: 'Roles',
    hint: 'A job title, e.g. Backend Developer',
    parent: 'category',
    fields: ['name', 'aliases'],
  },
  specialty: {
    label: 'Specialty',
    plural: 'Specialties',
    hint: 'A focus inside a role, e.g. Node.js',
    parent: 'role',
    fields: ['name'],
  },
  tool: {
    label: 'Tool',
    plural: 'Tools',
    hint: 'Software or equipment used in a specialty, e.g. Docker',
    parent: 'specialty',
    fields: ['name'],
  },
  skill: {
    label: 'Skill',
    plural: 'Skills',
    hint: 'A capability within a role, e.g. API design, or a generic one like Communication',
    parent: 'role',
    fields: ['name'],
    /* Can exist with no role at all (is_generic). */
    generic: true,
  },
};

export const KINDS = Object.keys(LIBRARY);

export const FIELDS = {
  name: { label: 'Name', placeholder: '', required: true },
  description: { label: 'Description', placeholder: 'One line, optional', required: false },
  aliases: {
    label: 'Also known as',
    placeholder: 'Comma separated, e.g. Back-end Engineer, Server Developer',
    required: false,
  },
};

/** Ancestors of a kind, top first: chain('tool') -> ['category','role','specialty'], chain('skill') -> ['category','role']. */
export const chain = (kind) => {
  const out = [];
  let k = LIBRARY[kind]?.parent;
  while (k) {
    out.unshift(k);
    k = LIBRARY[k].parent;
  }
  return out;
};

/** Status pill mapping onto the ui-x StatusChip kinds. */
export const STATUS_CHIP = {
  approved: { status: 'ready', label: 'Approved' },
  pending: { status: 'review', label: 'Pending' },
  rejected: { status: 'suspended', label: 'Rejected' },
  '': { status: 'draft', label: 'Unset' },
};

/** Turn an API error body into a sentence the admin can act on. */
export const describeError = (err, kind) => {
  const e = err?.data?.error;
  const label = (LIBRARY[kind]?.label || 'Item').toLowerCase();
  if (!e) return err?.data?.message || err?.error || 'Something went wrong. Please try again.';
  switch (e.code) {
    case 'DUPLICATE': {
      const where = e.existing?.parent ? ` under ${e.existing.parent}` : e.existing?.generic ? ' as a generic skill' : '';
      return `"${e.existing?.name}" already exists${where}. Pick a different name or edit the existing one.`;
    }
    case 'IS_GENERIC':
      return `"${e.existing?.name}" is a generic skill and is not tied to any role. Edit it and untick "generic" first if it should belong to this role.`;
    case 'NAME_TOO_SHORT':
      return `Name needs at least ${e.min} characters.`;
    case 'NAME_TOO_LONG':
      return `Name is too long (${e.max} characters max).`;
    case 'PARENT_REQUIRED':
      return `Choose a ${LIBRARY[e.parent]?.label.toLowerCase() || 'parent'} first.`;
    case 'PARENT_NOT_FOUND':
      return `That ${LIBRARY[e.parent]?.label.toLowerCase() || 'parent'} no longer exists. Refresh and try again.`;
    case 'IN_USE':
      return `This ${label} is used by ${e.openJobs} open job${e.openJobs === 1 ? '' : 's'} and cannot be deleted. Close those jobs first.`;
    case 'NOT_FOUND':
      return `That ${label} no longer exists.`;
    default:
      return e.message || `Could not save the ${label}.`;
  }
};
